import { Controller } from "@hotwired/stimulus"

import { wrenchMarkSvg } from "@/domains/orders/lib/canvas/rendering/wrench-mark"

/**
 * The floorplan's view modes, on the page side: the "View" menu, the legend
 * and the tooltip — and the URL, which is the only place a view is kept.
 *
 * It sits on the same element as `facilities--floorplan-canvas` and talks to
 * it through events on that element, never by reaching into it:
 *
 *   asks     `view-mode:request`  {state}   draw this
 *            `view-mode:prefetch`           the menu opened — fetch ahead
 *            `view-mode:retry`              the legend's Retry
 *   hears    `view-mode:changed`  {view}    what the plan now draws
 *            `view-mode:hover`    {tip}     what is under the pointer
 *
 * Like the device panel's controller, it writes VALUES and no words: every
 * word arrives translated in `wordsValue` (`Floorplans::ViewModeWords`), and
 * every colour in the canvas's own `view.legend`, so a swatch and the plan
 * cannot disagree. It names no design-system class either: what it shows and
 * hides it shows and hides with the `hidden` attribute.
 */

/** The URL parameters a view is kept in, and their defaults. */
const DEFAULTS = {
  view: "live",
  src: "impacts",
  sev: "warning,critical",
  out: "red",
  range: "30d",
  tmin: "-30",
  tmax: "30",
}

/** The legend's name for each battery verdict (`battery.ts`'s own mapping). */
const BATTERY_LEVEL_OF_TONE = {
  success: "ok",
  warning: "medium",
  danger: "low",
}

/**
 * The states with a live sensor (`device-state.ts`'s `isLive`). A device in
 * any other is drawn with no reading in every telemetry mode, so its tooltip
 * says no more.
 */
const LIVE_STATES = new Set(["online", "warning", "alert", "offline"])

/** Whether a telemetry mode draws a device in this state with no reading. */
function drawnUnread(live) {
  return Boolean(live) && !LIVE_STATES.has(live)
}

/**
 * The tooltip's gap from the point it is about, and the least room it keeps
 * from the plan's edges (px).
 */
const TOOLTIP_GAP = 10
const TOOLTIP_MARGIN = 8

/**
 * Live's low battery in the legend, as the plan draws it (CONEK-8872): its
 * dot, in `colour`, on the upper-right edge of a device's disc. The disc is a
 * neutral grey — the dot rides on any state, so no state's colour is its own.
 */
const LOW_BATTERY_DISC = "#cbd5e1"

function lowBatterySwatchSvg(colour) {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">' +
    `<circle cx="6" cy="8" r="5" fill="${LOW_BATTERY_DISC}"/>` +
    `<circle cx="10" cy="4" r="3.6" fill="${colour}" stroke="#ffffff" stroke-width="1.2"/>` +
    "</svg>"
  )
}

/**
 * How long the legend's fold is remembered (a year). The cookie's NAME is the
 * server's (`data-legend-cookie` on the fold), so the two cannot drift.
 */
const LEGEND_COOKIE_AGE = 60 * 60 * 24 * 365

/** An ISO8601 moment, in the page's own language — or null. */
function formatMoment(iso) {
  if (!iso) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  return at.toLocaleString(document.documentElement.lang || undefined)
}

/** `%{key}` filled, the value taken as it is. */
function fill(template, key, value) {
  return (template ?? "").replaceAll(`%{${key}}`, () => String(value))
}

export default class extends Controller {
  static targets = [
    "label",
    "legend",
    "legendTitle",
    "swatches",
    "swatchTemplate",
    "swatchGroupTemplate",
    "scale",
    "status",
    "retry",
    "tooltip",
    "tooltipRowTemplate",
    "temperatureMin",
    "temperatureMax",
    "historyRange",
  ]

  static values = {
    state: Object,
    words: Object,
  }

  connect() {
    this.current = this.stateValue
  }

  // ===========================================================================
  // Asking
  // ===========================================================================

  /**
   * The menu's radio group moved (`dropdown-menu:radio-change`). Picking a
   * view is the whole point of the menu, so it closes on the pick and hands
   * focus back to its trigger: the design system's menu keeps a radio group
   * open, for menus that set several at once.
   */
  pick(event) {
    if (event.detail?.name !== "floorplan-view") return
    this.#closeMenu(event.target)
    const mode = event.detail.value
    if (!mode || mode === this.current?.mode) return
    this.request({ ...this.current, mode })
  }

  /** The legend was unfolded (`accordion:open`): forget that it was folded. */
  legendOpened(event) {
    this.#rememberLegend(event.currentTarget?.dataset?.legendCookie, false)
  }

  /** The legend was folded to its title (`accordion:close`): keep it so. */
  legendClosed(event) {
    this.#rememberLegend(event.currentTarget?.dataset?.legendCookie, true)
  }

  /**
   * Escape inside the legend's body is its controls' — it closes a date
   * picker's calendar, a field gives up its edit — never the fold's. The fold
   * (`ui--accordion`) listens on the document and folds on any Escape while
   * focus is inside it, so the key stops here: closing a calendar must not
   * fold the legend under the reader's hands. Escape on the title still folds.
   *
   * The date picker closes its calendar on Escape only from its own field, so
   * with focus on its calendar icon or on a day (CONEK-8823) the calendar is
   * closed here, through the picker itself.
   */
  holdEscape(event) {
    const element = event.target.closest?.(
      "[data-controller~='ui--date-picker']",
    )
    const picker =
      element &&
      this.application.getControllerForElementAndIdentifier(
        element,
        "ui--date-picker",
      )
    if (picker?.isOpen()) picker.close()
    event.stopPropagation()
  }

  /** Draw `next`, and keep it in the URL. */
  request(next) {
    this.current = next
    this.#writeUrl(next)
    if (this.hasLabelTarget) {
      this.labelTarget.textContent =
        this.wordsValue.modes?.[next.mode] ?? next.mode
    }
    this.dispatch("request", {
      prefix: "view-mode",
      detail: { state: next },
    })
  }

  /**
   * The temperature scale's two ends, from the legend. Whole degrees, inside
   * the server's limits and in order; an end that would not be is put back to
   * what it was, so the inputs never show a scale the plan is not drawing.
   */
  setTemperature() {
    if (!this.hasTemperatureMinTarget || !this.hasTemperatureMaxTarget) return
    const current = this.current?.temperature ?? { min: -30, max: 30 }
    const read = (input, fallback) => {
      const value = Number.parseInt(input.value, 10)
      return Number.isFinite(value) && value >= -60 && value <= 100
        ? value
        : fallback
    }
    let min = read(this.temperatureMinTarget, current.min)
    let max = read(this.temperatureMaxTarget, current.max)
    if (min >= max) ({ min, max } = current)

    this.temperatureMinTarget.value = String(min)
    this.temperatureMaxTarget.value = String(max)
    if (min === current.min && max === current.max) return
    this.request({ ...this.current, temperature: { min, max } })
  }

  // --- History: what it counts, which of it, over which days ---------------

  /** Impacts, or inspections. */
  setSource(event) {
    const source = event.currentTarget?.dataset.historySource
    if (!source || source === this.current?.history?.source) return
    this.request({
      ...this.current,
      history: { ...this.current.history, source },
    })
  }

  toggleSeverity(event) {
    this.#toggle(event.currentTarget?.dataset.historySeverity, "severities", [
      "warning",
      "critical",
    ])
  }

  /** The one outcome inspections are counted by, as legacy's Damage History. */
  setOutcome(event) {
    const outcome = event.currentTarget?.dataset.historyOutcome
    if (!outcome || outcome === this.current?.history?.outcome) return
    this.request({
      ...this.current,
      history: { ...this.current.history, outcome },
    })
  }

  /**
   * One filter on or off, kept in its own order. Never the last one off: a
   * filter that hides everything is never what somebody meant.
   */
  #toggle(value, key, order) {
    if (!value) return
    const list = this.current?.history?.[key] ?? order
    const next = list.includes(value)
      ? list.filter((item) => item !== value)
      : order.filter((item) => item === value || list.includes(item))
    if (next.length === 0) return
    this.request({
      ...this.current,
      history: { ...this.current.history, [key]: next },
    })
  }

  /**
   * A range preset. "Custom" opens the two pickers and asks nothing until
   * both days are there; every other preset drops a custom range's days.
   */
  setPreset(event) {
    const preset = event.currentTarget?.dataset.historyPreset
    if (!preset) return
    const history = { ...this.current.history, preset }
    if (preset === "custom") {
      this.current = { ...this.current, history }
      this.#syncHistoryControls(this.current)
      if (!history.from || !history.to || history.from > history.to) return
      this.request(this.current)
      return
    }
    this.request({
      ...this.current,
      history: { ...history, from: null, to: null },
    })
  }

  /**
   * A custom range's day, from its picker (`ui--date-picker:change`).
   *
   * Read from the picker's hidden `history_from` / `history_to` field, not the
   * event: `detail.value` is the day as the reader's locale writes it
   * (`01/10/2026`), and only the hidden field is always ISO.
   */
  setRange(event) {
    const wrapper = event.target?.closest?.("[data-history-date]")
    const side = wrapper?.dataset.historyDate
    const value = wrapper?.querySelector(`input[name="history_${side}"]`)?.value
    if (!side || !/^\d{4}-\d{2}-\d{2}$/.test(value ?? "")) return
    const history = { ...this.current.history, preset: "custom", [side]: value }
    this.current = { ...this.current, history }
    if (history.from && history.to && history.from <= history.to) {
      this.request(this.current)
    }
  }

  prefetch() {
    this.dispatch("prefetch", { prefix: "view-mode" })
  }

  retry() {
    this.dispatch("retry", { prefix: "view-mode" })
  }

  // ===========================================================================
  // What the plan draws
  // ===========================================================================

  /** `view-mode:changed` — the legend, the trigger, and the state it holds. */
  show(event) {
    const view = event.detail
    if (!view) return

    // The canvas may have refused a mode this person cannot read (it draws
    // `live` instead); what it says it draws is what the page says too.
    if (view.state) this.current = view.state
    if (this.hasLabelTarget) {
      this.labelTarget.textContent =
        this.wordsValue.modes?.[view.mode] ?? view.mode
    }

    this.#renderLegend(view)
  }

  #renderLegend(view) {
    if (!this.hasLegendTarget) return
    const words = this.wordsValue.legend ?? {}
    const drawn = view.drawn

    if (this.hasLegendTitleTarget) {
      this.legendTitleTarget.textContent =
        this.wordsValue.modes?.[drawn] ?? drawn
    }

    // One row per colour the drawn mode uses; a group's rows under its
    // heading, after the rest (Live's damage awaiting repair).
    if (this.hasSwatchesTarget && this.hasSwatchTemplateTarget) {
      const names = words[drawn] ?? {}
      let group = null
      const rows = (view.legend?.swatches ?? []).flatMap((swatch) => {
        const row =
          this.swatchTemplateTarget.content.firstElementChild.cloneNode(true)
        const dot = row.querySelector("[data-swatch]")
        dot.style.borderColor = swatch.colour
        dot.style.backgroundColor = swatch.hollow
          ? "transparent"
          : swatch.colour
        // A mark drawn ON a device, not a device's own colour, drawn as the
        // plan draws it in the swatch's place: Live's low-battery dot, and its
        // damage awaiting repair (legacy's wrench mark, in a risk colour).
        if (swatch.dot || swatch.wrench) {
          Object.assign(dot.style, {
            borderWidth: "0",
            backgroundColor: "transparent",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "visible",
          })
          dot.innerHTML = swatch.wrench
            ? wrenchMarkSvg(swatch.colour, 14)
            : lowBatterySwatchSvg(swatch.colour)
        }
        row.querySelector("[data-swatch-label]").textContent =
          swatch.key === "no_data"
            ? (words.no_data ?? "")
            : (names[swatch.key] ?? swatch.key)
        const heading =
          swatch.group &&
          swatch.group !== group &&
          this.hasSwatchGroupTemplateTarget
            ? this.swatchGroupTemplateTarget.content.firstElementChild.cloneNode(
                true,
              )
            : null
        if (heading) heading.textContent = names[swatch.group] ?? swatch.group
        group = swatch.group ?? null
        return heading ? [heading, row] : [row]
      })
      this.swatchesTarget.replaceChildren(...rows)
      this.swatchesTarget.hidden = rows.length === 0
    }

    // A continuous scale: a bar of its colours and its two ends.
    if (this.hasScaleTarget) {
      const scale = view.legend?.scale
      this.scaleTarget.hidden = !scale
      if (scale) {
        this.scaleTarget.querySelector("[data-scale-bar]").style.background =
          `linear-gradient(to right, ${scale.stops.join(", ")})`
        this.scaleTarget.querySelector("[data-scale-min]").textContent =
          this.#scaleValue(drawn, scale.min)
        this.scaleTarget.querySelector("[data-scale-max]").textContent =
          this.#scaleValue(drawn, scale.max)
      }
    }

    // Loading, failed, or when the plan was last told.
    if (this.hasStatusTarget) {
      let status = ""
      if (view.status === "loading") status = words.loading ?? ""
      else if (view.status === "error") status = words.error ?? ""
      else if (view.updatedAt)
        status = fill(words.updated, "time", formatMoment(view.updatedAt))
      this.statusTarget.textContent = status
    }
    if (this.hasRetryTarget) this.retryTarget.hidden = view.status !== "error"

    // The legend's controls and notes for the drawn mode only — a section
    // names the modes it belongs to.
    for (const section of this.legendTarget.querySelectorAll(
      "[data-view-section]",
    )) {
      section.hidden = !section.dataset.viewSection.split(" ").includes(drawn)
    }

    if (view.state) this.#syncHistoryControls(view.state)
    if (this.hasHistoryRangeTarget) {
      this.historyRangeTarget.textContent = view.range
        ? `${view.range.label} · ${view.range.timeZone}`
        : ""
    }
  }

  /**
   * The history controls as `state` has them: which buttons are pressed,
   * which filters show, whether the custom pickers do. Attributes only.
   */
  #syncHistoryControls(state) {
    if (!this.hasLegendTarget) return
    const history = state.history ?? {}
    const press = (selector, read, on) => {
      for (const button of this.legendTarget.querySelectorAll(selector)) {
        const pressed = on(read(button))
        button.dataset.active = String(pressed)
        button.setAttribute("aria-pressed", String(pressed))
      }
    }
    press(
      "[data-history-source]",
      (b) => b.dataset.historySource,
      (value) => value === history.source,
    )
    press(
      "[data-history-severity]",
      (b) => b.dataset.historySeverity,
      (value) => (history.severities ?? []).includes(value),
    )
    press(
      "[data-history-outcome]",
      (b) => b.dataset.historyOutcome,
      (value) => value === history.outcome,
    )
    press(
      "[data-history-preset]",
      (b) => b.dataset.historyPreset,
      (value) => value === history.preset,
    )

    for (const filters of this.legendTarget.querySelectorAll(
      "[data-history-filters]",
    )) {
      filters.hidden = filters.dataset.historyFilters !== history.source
    }
    const custom = this.legendTarget.querySelector("[data-history-custom]")
    if (custom) custom.hidden = history.preset !== "custom"
  }

  /** One end of a scale, as the mode writes its values. */
  #scaleValue(mode, value) {
    if (mode === "temperature") return `${value} °C`
    return String(value)
  }

  // ===========================================================================
  // The tooltip
  // ===========================================================================

  /** `view-mode:hover` — what is under the pointer, or nothing. */
  hover(event) {
    if (!this.hasTooltipTarget) return
    const tip = event.detail
    const content = tip ? this.#tipContent(tip) : null
    if (!content || (!content.title && content.rows.length === 0)) {
      this.tooltipTarget.hidden = true
      return
    }

    this.tooltipTarget.querySelector("[data-tooltip-title]").textContent =
      content.title ?? ""
    const hint = this.tooltipTarget.querySelector("[data-tooltip-hint]")
    if (hint) {
      hint.textContent = content.hint ?? ""
      hint.hidden = !content.hint
    }
    const rows = this.tooltipTarget.querySelector("[data-tooltip-rows]")
    rows.replaceChildren(
      ...content.rows.flatMap(([label, value]) => {
        const [dt, dd] = [
          ...this.tooltipRowTemplateTarget.content.cloneNode(true).children,
        ]
        dt.textContent = label
        dd.textContent = value
        return [dt, dd]
      }),
    )
    // Shown before it is placed: a hidden tooltip has no size to place by.
    // Both happen in this task, so nothing is painted in between.
    this.tooltipTarget.hidden = false
    this.#place(tip.x, tip.y, tip.clear ?? 0)
  }

  /**
   * Over the point and centred on it, clear of the mark drawn there (`clear`
   * px round it), inside the plan's frame: nudged in from a side it would
   * cross, and under the mark when there is no room above — so it never covers
   * what it is about, and whatever it says is never cut off by the frame's
   * edge or squeezed into the room left beside it. A frame with no size (not
   * laid out) has nothing to keep it inside, and it sits over the point as it
   * is.
   */
  #place(x, y, clear) {
    const tooltip = this.tooltipTarget
    const frame = tooltip.offsetParent
    const width = tooltip.offsetWidth
    const height = tooltip.offsetHeight
    let left = x - width / 2
    let top = y - clear - TOOLTIP_GAP - height
    if (frame?.clientWidth > 0) {
      const maxLeft = frame.clientWidth - width - TOOLTIP_MARGIN
      left = Math.max(TOOLTIP_MARGIN, Math.min(left, maxLeft))
      if (top < TOOLTIP_MARGIN) top = y + clear + TOOLTIP_GAP
      const maxTop = frame.clientHeight - height - TOOLTIP_MARGIN
      top = Math.max(TOOLTIP_MARGIN, Math.min(top, maxTop))
    }
    tooltip.style.left = `${Math.round(left)}px`
    tooltip.style.top = `${Math.round(top)}px`
  }

  /** What the tooltip says about a point, in the mode the plan draws. */
  #tipContent(tip) {
    switch (tip.mode) {
      case "battery":
        return this.#batteryTip(tip.reading, tip.live)
      case "signal":
        return this.#signalTip(tip.reading, tip.live)
      case "temperature":
        return this.#temperatureTip(tip.reading, tip.live)
      case "inspection":
        return this.#inspectionTip(tip.reading)
      case "history":
        return this.#historyTip(tip.reading)
      default:
        return this.#liveTip(tip)
    }
  }

  /**
   * A unit's battery: its verdict, and its millivolts. A unit not reporting
   * now is offline — its title, as in signal and temperature — and its
   * verdict, greyed on the server, goes unnamed; the low flag, which still
   * draws it low (`battery.ts`), is a row under that. Online, the flag IS the
   * verdict, so a row for it would only repeat the title.
   */
  #batteryTip(reading, live) {
    const words = this.wordsValue.tooltip ?? {}
    const battery = reading?.battery
    // A unit can report a signal and a temperature and nothing about its
    // battery (a SmartCap posts none). Its reading time is then the time of
    // those, and under "No reading" it would date a battery reading that does
    // not exist — so it says no more than signal and temperature do.
    if ((!battery && !reading?.lowBattery) || drawnUnread(live))
      return { title: words.no_reading ?? null, rows: [] }
    const rows = []
    if (typeof battery?.millivolts === "number")
      rows.push([words.battery, `${this.#number(battery.millivolts)} mV`])
    let title
    if (reading.online) {
      const level = reading.lowBattery
        ? "low"
        : BATTERY_LEVEL_OF_TONE[battery?.tone]
      title = (level && words.battery_levels?.[level]) ?? words.no_reading
    } else {
      title = words.states?.offline
      if (reading.lowBattery && words.low_battery)
        rows.push([words.low_battery, ""])
    }
    this.#readingRow(rows, reading)
    return { title: title ?? null, rows }
  }

  /**
   * A unit's signal: its bucket, and the dBm it came from. A unit not
   * reporting now is drawn with no reading (`signal.ts`): it is offline, and
   * the dBm is what it last reported — no bucket, which would say how well it
   * reaches its gateway now.
   */
  #signalTip(reading, live) {
    const words = this.wordsValue.tooltip ?? {}
    if (!reading || reading.signal === "unknown" || drawnUnread(live))
      return { title: words.no_reading ?? null, rows: [] }
    const rows = []
    if (typeof reading.rssiDbm === "number")
      rows.push([words.signal, `${this.#number(reading.rssiDbm)} dBm`])
    this.#readingRow(rows, reading)
    return {
      title: reading.online
        ? (words.signal_buckets?.[reading.signal] ?? reading.signal)
        : (words.states?.offline ?? null),
      rows,
    }
  }

  /**
   * A unit's temperature, and when it was measured. A unit not reporting now
   * is drawn with no reading (`temperature.ts`): it is offline, and the
   * temperature is a row — what it last measured, not what it measures now.
   */
  #temperatureTip(reading, live) {
    const words = this.wordsValue.tooltip ?? {}
    if (
      !reading ||
      typeof reading.temperatureC !== "number" ||
      drawnUnread(live)
    )
      return { title: words.no_reading ?? null, rows: [] }
    const celsius = `${this.#number(reading.temperatureC, 1)} °C`
    const rows = []
    if (!reading.online) rows.push([words.temperature ?? "", celsius])
    this.#readingRow(rows, reading)
    return {
      title: reading.online ? celsius : (words.states?.offline ?? null),
      rows,
    }
  }

  /**
   * A leg's inspection standing, and when it was last inspected. The standing
   * is decided as the canvas draws it: a risk on record wins, and only a leg
   * with neither a risk nor an inspection was never inspected.
   */
  #inspectionTip(reading) {
    const words = this.wordsValue.tooltip ?? {}
    if (!reading) return { title: words.no_reading ?? null, rows: [] }
    const risk = reading.riskLevel
    const standing =
      risk && risk !== "no_maintenance_required"
        ? risk
        : reading.lastInspectedAt
          ? "no_maintenance_required"
          : "never"
    const rows = []
    const at = formatMoment(reading.lastInspectedAt)
    if (at) rows.push([words.last_inspected ?? "", at])
    let title = words.risk_levels?.[standing] ?? standing
    // CONEK-9038 — the system made this Amber walk Red; the plan says so in
    // the tooltip's title ("Red risk (Escalated)"). Only while the leg is Red
    // (plan R4): the same rule as `standingOf` in the canvas's
    // view-modes/inspection.ts, which this controller cannot import (it loads
    // without Vite) — change both together; view_mode_controller.test.ts
    // fails if they disagree.
    const escalated =
      reading.escalated && risk === "red_risk"
        ? words.risk_levels?.escalated
        : null
    if (escalated) title = `${title} (${escalated})`
    return { title, rows }
  }

  /** A leg's counts in the range, under the filters picked. */
  #historyTip(reading) {
    const words = this.wordsValue.tooltip ?? {}
    const history = this.current?.history
    if (!reading || !history)
      return { title: words.no_reading ?? null, rows: [] }
    const inspections = history.source === "inspections"
    const keys = inspections ? [history.outcome] : history.severities
    const counts = (inspections ? reading.inspections : reading.impacts) ?? {}
    const rows = (keys ?? []).map((key) => [
      words.history?.[key] ?? key,
      this.#number(counts[key] ?? 0),
    ])
    const total = (keys ?? []).reduce((sum, key) => sum + (counts[key] ?? 0), 0)
    return {
      title: `${this.#number(total)} · ${words.history?.[history.source] ?? history.source}`,
      rows,
    }
  }

  /** When the reading was taken — a legacy RackEye's can be hours old. */
  #readingRow(rows, reading) {
    const at = formatMoment(reading?.lastTelemetryAt)
    if (at) rows.push([this.wordsValue.tooltip?.reading_at ?? "", at])
  }

  /** A number in the page's language, to at most `digits` decimals. */
  #number(value, digits = 0) {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      maximumFractionDigits: digits,
    }).format(value)
  }

  /**
   * Live: the device's state, what it means in a sentence (the one word alone
   * reads as jargon), and the facts it hides — a struck leg whose unit then
   * went quiet, a low battery under an alert, damage an inspection found that
   * is still awaiting repair (its wrench mark). The two times close it: when
   * that inspection was, then when the unit last reported, worded as the
   * other modes word it.
   */
  #liveTip(tip) {
    const words = this.wordsValue.tooltip ?? {}
    const facts = tip.facts ?? {}
    const rows = []
    const attention = tip.live === "alert" || tip.live === "warning"

    if (attention && facts.online === false && words.not_reporting) {
      rows.push([words.not_reporting, ""])
    }
    if (facts.lowBattery && words.low_battery)
      rows.push([words.low_battery, ""])
    const risk = tip.reading?.riskLevel
    if (risk && words.pending_damage)
      rows.push([words.pending_damage, words.risk_levels?.[risk] ?? risk])
    if (facts.alertCount) rows.push([words.alerts, String(facts.alertCount)])
    if (facts.warningCount)
      rows.push([words.warnings, String(facts.warningCount)])
    const inspected = risk ? formatMoment(tip.reading?.lastInspectedAt) : null
    if (inspected) rows.push([words.last_inspected ?? "", inspected])
    const seen = formatMoment(facts.lastSeen)
    if (seen) rows.push([words.reading_at ?? "", seen])

    // One line each: what the state means, then what the wrench means.
    const hint = [
      tip.live ? words.state_hints?.[tip.live] : null,
      risk ? words.pending_damage_hint : null,
    ]
      .filter(Boolean)
      .join("\n")
    return {
      title: tip.live ? (words.states?.[tip.live] ?? null) : null,
      hint: hint || null,
      rows,
    }
  }

  // ===========================================================================
  // The URL
  // ===========================================================================

  /**
   * The view, in the address bar — a reload or a shared link opens on it.
   * `replaceState`, not a visit: changing what the plan shows is not a page
   * of its own in the history, and Turbo's restoration id in `history.state`
   * is kept as it is.
   */
  #writeUrl(state) {
    const url = new URL(window.location.href)
    const params = {
      view: state.mode,
      src: state.history?.source,
      sev: state.history?.severities?.join(","),
      out: state.history?.outcome,
      range: state.history?.preset,
      tmin: state.temperature?.min,
      tmax: state.temperature?.max,
    }
    for (const [key, value] of Object.entries(params)) {
      const text = value === undefined || value === null ? "" : String(value)
      if (text === "" || text === DEFAULTS[key]) url.searchParams.delete(key)
      else url.searchParams.set(key, text)
    }
    const custom = state.history?.preset === "custom"
    for (const key of ["from", "to"]) {
      const value = custom ? state.history?.[key] : null
      if (value) url.searchParams.set(key, value)
      else url.searchParams.delete(key)
    }
    window.history.replaceState(window.history.state, "", url)
  }

  // ===========================================================================
  // The menu and the legend's fold
  // ===========================================================================

  /**
   * Close the `ui--dropdown-menu` that announced a pick, if it is one. Its
   * `closeMenu()` is the design system's; if a conek-ui release ever drops
   * it, the menu would stay open, so that says so in the console rather than
   * nowhere (the Vitest suite registers the gem's real controller to catch it
   * before a bump ships).
   */
  #closeMenu(source) {
    const menu = source?.closest?.('[data-controller~="ui--dropdown-menu"]')
    if (!menu) return
    const controller = this.application.getControllerForElementAndIdentifier(
      menu,
      "ui--dropdown-menu",
    )
    if (typeof controller?.closeMenu === "function") controller.closeMenu()
    else if (controller)
      console.warn(
        "floorplans--view-mode: ui--dropdown-menu has no closeMenu(); the View menu stays open",
      )
  }

  /**
   * The legend's fold, for the next page to open as it was left: a cookie the
   * server reads (`Floorplans::ViewLegendComponent::LEGEND_COOKIE`, written on
   * the fold as `data-legend-cookie`), so the page renders it folded rather
   * than folding it after the first paint. No name, nothing kept.
   */
  #rememberLegend(cookie, collapsed) {
    if (!cookie) return
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = collapsed
      ? `${cookie}=collapsed; path=/; max-age=${LEGEND_COOKIE_AGE}; SameSite=Lax${secure}`
      : `${cookie}=; path=/; max-age=0; SameSite=Lax${secure}`
  }
}
